import { useQuery } from "@apollo/client";
import { PROPOSAL } from "modules/custom/api";
import { useDraftContext } from "./DraftContext";
import { Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiCard from "components/base/UiCard";
import UiSideModal from "components/base/UiSideModal";
import { CustomGetProposalQueryData } from "hooks/proposals/useCustomGetProposal";
import useModalState from "hooks/useModalState";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import DraftDiscountDrawer from "./DraftDiscountDrawer";
import DraftSalesTax from "./DraftSalesTax";
import LabeledValue from "./LabeledValue";
import MarginsDrawer from "./MarginsDrawer";
import { draftColors } from "./draftDesignTokens";

type FinancialsCardProps = {
  proposal: NonNullable<CustomGetProposalQueryData>;
};

export default function FinancialsCard({ proposal }: FinancialsCardProps) {
  const { estimatingMethod } = useDraftContext();
  const isWorksheet = estimatingMethod === "worksheet";
  const hasCustom = proposal.stages?.some((stage) =>
    stage.proposalProducts?.some((item) => item.isCustom)
  );
  const { data: customData } = useQuery(PROPOSAL, {
    variables: { id: proposal.id },
    skip: !hasCustom,
  });
  const financials = customData?.customProposal?.customFinancials;
  const incomplete = hasCustom && (!financials || financials.incomplete);
  const taxDrawer = useModalState();
  const discountDrawer = useModalState();

  const directCosts = formatCentsToUSD(proposal.costDirect ?? 0);
  const overheadCosts = formatCentsToUSD(proposal.costOverhead ?? 0);
  const indirectCosts = formatCentsToUSD(proposal.costIndirect ?? 0);
  const costTotal = formatCentsToUSD(proposal.costTotal ?? 0);

  const profitPercentage = proposal.profitPercentage ?? 0;
  const discountCents = +(proposal.discount ?? 0);
  const discount = formatCentsToUSD(discountCents);
  const revenue = +(proposal.overallTotal ?? 0) - discountCents;
  const netMargin = revenue > 0 ? ((revenue - +(proposal.costTotal ?? 0)) / revenue) * 100 : 0;
  const profit = formatCentsToUSD(+(proposal.profit ?? 0) - discountCents);
  const subtotal = formatCentsToUSD(+(proposal.overallTotal ?? 0));

  const taxAmount = +(proposal.costTaxes ?? 0);
  const overallTotalAfterTaxes =
    proposal.overallTotalAfterTaxes ?? +(proposal.overallTotal ?? 0) - discountCents;
  const total = formatCentsToUSD(overallTotalAfterTaxes);

  return (
    <>
      <UiCard variant="info">
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap="8px">
          <Grid container spacing="12px" sx={{ flex: 1, minWidth: 0 }}>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" flexDirection="column" gap="6px">
                <LabeledValue label="Direct Costs" value={directCosts} boldValue />
                <LabeledValue label="Overhead Costs" value={overheadCosts} />
                <LabeledValue label="Indirect Costs" value={indirectCosts} />
                <LabeledValue
                  label={incomplete ? "Known costs (incomplete)" : "Total Cost"}
                  value={costTotal}
                  boldValue
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" flexDirection="column" gap="6px">
                <LabeledValue
                  label={isWorksheet || hasCustom ? "Profit" : `Profit (${profitPercentage}%)`}
                  value={incomplete ? "Unavailable" : profit}
                  boldValue
                />
                {isWorksheet && (
                  <LabeledValue label="Net Margin" value={`${netMargin.toFixed(2)}%`} />
                )}
                {!isWorksheet && hasCustom && (
                  <LabeledValue
                    label="Net Margin"
                    value={incomplete ? "Unavailable" : `${financials?.netMargin ?? "0.00"}%`}
                  />
                )}
                <LabeledValue label="Subtotal" value={subtotal} boldValue />
                <LabeledValue
                  label={
                    <MDBox
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={discountDrawer.onOpen}
                    >
                      Discount
                      <Icon
                        sx={{
                          fontSize: "12px !important",
                          color: draftColors.textSecondary,
                        }}
                      >
                        edit
                      </Icon>
                    </MDBox>
                  }
                  value={discount}
                />
                <LabeledValue
                  label={
                    <MDBox
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={taxDrawer.onOpen}
                    >
                      Tax
                      <Icon
                        sx={{
                          fontSize: "12px !important",
                          color: draftColors.textSecondary,
                        }}
                      >
                        edit
                      </Icon>
                    </MDBox>
                  }
                  value={formatCentsToUSD(taxAmount)}
                />
                <LabeledValue label="Total" value={total} boldValue />
              </MDBox>
            </Grid>
          </Grid>
          {!isWorksheet && <MarginsDrawer proposal={proposal} />}
        </MDBox>
      </UiCard>

      <UiSideModal
        open={taxDrawer.open}
        onClose={taxDrawer.onClose}
        title="Sales Tax"
        width="420px"
      >
        <DraftSalesTax onClose={taxDrawer.onClose} />
      </UiSideModal>

      <DraftDiscountDrawer
        open={discountDrawer.open}
        onClose={discountDrawer.onClose}
        proposal={proposal}
        incompleteCosts={incomplete}
        hasLinkedTargets={customData?.customProposal?.stages?.some((stage: any) =>
          stage.proposalProducts?.some((product: any) =>
            ["profit", "margin"].includes(product.custom?.pricing?.mode)
          )
        )}
      />
    </>
  );
}
